// Define environment types
type Environment = 'development' | 'staging' | 'production';

// Immediate logging of raw environment values
console.log('🌍 Raw Environment Values:', {
  hostname: window.location.hostname,
  href: window.location.href,
  isDev: import.meta.env.DEV,
  mode: import.meta.env.MODE,
  deployEnv: import.meta.env.VITE_DEPLOY_ENV,
  baseUrl: import.meta.env.BASE_URL,
  timestamp: new Date().toISOString(),
});

// API URLs for each environment
const API_URLS = {
  development: 'http://localhost:8787',
  staging: 'https://staging-api.my-workouts.app',
  production: 'https://api.my-workouts.app',
} as const;

// Frontend URLs for each environment
const FRONTEND_URLS: Record<Environment, readonly string[]> = {
  development: ['localhost', '127.0.0.1'],
  staging: ['staging.my-workouts.app', 'my-workouts-staging.pages.dev'],
  production: ['my-workouts.app', 'www.my-workouts.app'],
} as const;

// Debug environment detection
console.log('🔍 Environment Detection:', {
  hostname: window.location.hostname,
  href: window.location.href,
  mode: import.meta.env.MODE,
  buildTime: new Date().toISOString(),
});

// Determine the environment
const getEnvironment = (): Environment => {
  const hostname = window.location.hostname;

  // Check each environment's URLs
  for (const [env, urls] of Object.entries(FRONTEND_URLS)) {
    if (urls.includes(hostname)) {
      console.log(`✅ Using ${env} environment`);
      return env as Environment;
    }
  }

  // Development environment check
  if (import.meta.env.DEV) {
    console.log('✅ Using development environment (DEV mode)');
    return 'development';
  }

  // Default to staging for safety
  console.log('⚠️ Unknown environment, defaulting to staging');
  return 'staging';
};

const ENV = getEnvironment();
// Use explicit API URL from environment variable if provided, otherwise use environment-based URL
const apiUrl = import.meta.env.VITE_API_URL || API_URLS[ENV];

// Log final configuration
console.log('⚡ Final Configuration:', {
  environment: ENV,
  apiUrl,
  explicitApiUrl: import.meta.env.VITE_API_URL,
  hostname: window.location.hostname,
  buildTime: new Date().toISOString(),
});

// Export frozen configuration
export const config = Object.freeze({
  apiUrl,
  environment: ENV,
  isProduction: ENV === 'production',
  isStaging: ENV === 'staging',
  isDevelopment: ENV === 'development',
});
